import { ReactElement, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useFetch } from "../../hooks/useFetch";
import VeriticalCenter from "../../components/atoms/VerticalCenter";
import Container from "../../components/atoms/Container";
import Info from "../../components/atoms/ErrorComponent";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import DisplayMountain from "../../components/organisms/Home/DisplayMountain";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { postUserGamification } from "../../services/user.service";
import PointModal from "../Profile/PointsModal";
import { ComponentType } from "../../types/Questionnaire";
import { getQuestionnairesByComponents } from "../../services/questionnaire.service";
import StoryTelling from "../../components/molecules/Home/StoryTellingModal";
import DisplayThankYouParticipation from "../../components/organisms/Invitation/DisplayThankYouParticipation";
import { UserGamificationDetails } from "../../types/welcome";
import { isMandatoryQuestionnaire } from "../../helper/questionnaire";
import { useHistory, useParams } from "react-router";
import IslandMap from "../MentalPlusPages/IslandMap";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";


const LoguedHome = (): ReactElement => {

  const { study } = useConfig();
  const { user } = useAuth();
  const {
    t,
    i18n: { language: lang },
  } = useTranslation();
  const history = useHistory();
  const { study_slug } = useParams<{ study_slug: string }>();

  // State for gamification ONLY
  const [pointToDisplay, setPointToDisplay] = useState(0);
  const [animationCompleted, setAnimationCompleted] = useState(false);
  const [isPointModalOpen, setIsPointModalOpen] = useState(false);
  const [isThankYouParticipationModalOpen, setIsThankYouParticipationModalOpen] = useState(false);
  const [isStoryTellingModalOpen, setIsStoryTellingModalOpen] = useState(false);
  const [disableHeaderFinished, setDisableHeaderFinished] = useState(false);
  const [storyTellingFinished, setStoryTellingFinished] = useState(false);

  // Only use context and fetches for gamified studies!
  const resonanceLayout = useResonanceLayout();
  const { setResonanceLayout, userGamificationDetails, questionnaires } = resonanceLayout || {};

  // Gamification fetch: ONLY call/use if gamified
  const gamificationFetch = study.is_gamified
    ? useFetch<ComponentType[]>("questionnairesBDD", getQuestionnairesByComponents, {
        studyId: study.id,
        userId: user.id,
        lang,
      })
    : { isLoading: false, isError: false, data: null };


  // Gamification branch effects
  // useEffect(() => {
  //   if (!study.is_gamified) return;
  //   if (gamificationFetch.data && questionnaires === undefined) {
  //     setResonanceLayout({ questionnaires: gamificationFetch.data });
  //   }
  // }, [gamificationFetch.data, questionnaires, setResonanceLayout, study.is_gamified]);

  // useEffect(() => {
  //   if (!study.is_gamified) return;
  //   setResonanceLayout({
  //     disableHeader: true,
  //     disableNav: false,
  //     backgroundColor: "white",
  //     welcomePage: false,
  //   });
  //   setDisableHeaderFinished(true);
  //   return () => {
  //     setResonanceLayout({
  //       disableHeader: false,
  //       disableNav: false,
  //       backgroundColor: "white",
  //       welcomePage: false,
  //     });
  //   };
  // }, [setResonanceLayout]);

  

  useEffect(() => {
    if (!study.is_gamified) return;
    if (userGamificationDetails === undefined) {
      const updateUserGamification = async () => {
        try {
          const { data: userGamificationBDD } = await postUserGamification(study.id, user.id, null);
          if (userGamificationBDD) {
            const userGamificationUpdates: UserGamificationDetails = {
              points: userGamificationBDD.points,
              step: userGamificationBDD.step,
              animation_success: userGamificationBDD.animation_success,
              pointsEarned: 0,
              stepEarned: 0,
              animation_successEarned: 0,
            };
            setResonanceLayout({ userGamificationDetails: userGamificationUpdates });
          }
        } catch (e) {
          console.error("Error updating user gamification: ", e);
        }
      };
      updateUserGamification();
    }
  }, [userGamificationDetails, setResonanceLayout, study.id, user.id, study.is_gamified]);

  // Other gamification-specific effects
  const handleAnimationComplete = (value: boolean) => {
    setAnimationCompleted(value);
  };

  useEffect(() => {
    if (!study.is_gamified) return;
    if (animationCompleted && pointToDisplay > 0) {
      setIsPointModalOpen(true);
    }
  }, [animationCompleted, pointToDisplay, study.is_gamified]);

  const checkPointEarned = () => {
    if (userGamificationDetails?.pointsEarned > 0) {
      setPointToDisplay(userGamificationDetails.pointsEarned);
      if (userGamificationDetails.animation_success === 0) {
        setIsThankYouParticipationModalOpen(true);
      }
    }
  };

  const checkStoryTelling = () => {
    if (userGamificationDetails?.points === 0 && userGamificationDetails?.step === 0) {
      setIsStoryTellingModalOpen(true);
    } else {
      setIsStoryTellingModalOpen(false);
      setStoryTellingFinished(true);
    }
  };

  useEffect(() => {
    if (!study.is_gamified) return;
    if (userGamificationDetails) {
      checkPointEarned();
      checkStoryTelling();
    }
  }, [userGamificationDetails, study.is_gamified]);

  useEffect(() => {
    if (!study.is_gamified) return;
    if (storyTellingFinished && questionnaires && userGamificationDetails) {
      for (const questionnaire of questionnaires) {
        if (isMandatoryQuestionnaire(questionnaire.keywords)) {
          history.push(
            `/${study_slug}/sequences/${questionnaire.sequenceId}/components/${questionnaire.id}`
          );
          break;
        }
      }
    }
  }, [questionnaires, storyTellingFinished, userGamificationDetails, history, study_slug, study.is_gamified]);

  // ---------------------
  // RENDERING LOGIC
  // ---------------------
  if (study.is_gamified) {
    if (gamificationFetch.isLoading) {
      return <ResonanceLoader />;
    }
    if (gamificationFetch.isError) {
      return (
        <Info
          title={t("common_server_error_title")}
          description={t("common_server_error_desc")}
          actionText={t("common_go_back")}
        />
      );
    }

    return (
      <Container>
        <VeriticalCenter>
          {isThankYouParticipationModalOpen ? (
            <DisplayThankYouParticipation onDismiss={() => setIsThankYouParticipationModalOpen(false)} />
          ) : (
            <>
              <PointModal
                isOpen={isPointModalOpen}
                onDismiss={() => setIsPointModalOpen(false)}
                points={pointToDisplay}
              />
              <StoryTelling
                isOpen={isStoryTellingModalOpen}
                onDismiss={() => setIsStoryTellingModalOpen(false)}
                animate={() => {
                  setIsStoryTellingModalOpen(false);
                  setStoryTellingFinished(true);
                }}
              />
              <DisplayMountain
                onAnimationComplete={handleAnimationComplete}
                disableHeaderFinished={disableHeaderFinished}
              />
            </>
          )}
        </VeriticalCenter>
      </Container>
    );
  } else {
    // --------- Non-gamified flow (Mentalplus) ---------
    return (
        <IslandMap />

    );
  };
};

export default LoguedHome;
